/**
 * The finishing pig, in two builds: `finisher` asleep, `finisher-herd` standing.
 *
 * These animals are not characters: their even dye and machine-neat seam are
 * repeated through the hall. The pose has to do the small amount of life needed
 * to distinguish a sleeping heap from stored stock, without adding a face rig,
 * repairs, marks, or the farrowing sow's teats.
 *
 * ── Why two definitions and one file ────────────────────────────────────────
 *
 * `finisher` is authored asleep on its side: `build()` assembles the animal
 * standing and then turns the whole thing over, and its `footprint` is the
 * MEASURED lying box that several rooms are dressed against. Beat 10 needs the
 * same animal on its feet, twelve at a time in a hedgerow at night, at a
 * fraction of the triangles — see `finisherHerdAsset` below for the arithmetic
 * that forces that. Neither of those is a flag on the other: the pose is baked
 * into the assembly and the detail level is baked into the segment counts.
 *
 * They share this file so that a pig's proportions are stated ONCE.
 * `FINISHER_SCALE`, `BODY`, `HEAD` and `LEG` are read by both builds, and the
 * builders below take their tessellation as an argument rather than owning it,
 * so the cheap pig is the same animal and not a second guess at one. Anything
 * that would have to be copied to split this into two files is the reason it is
 * not two files.
 */

import * as THREE from 'three';
import { FINISHER } from '@/render/palette';
import { surface } from './materials';
import { seatOnOrigin } from './origin';
import type { AssetDefinition } from './types';

/** Real standing dimensions in metres; the footprint below states their lying axes. */
export const FINISHER_SCALE = { length: 1.15, width: 0.34, height: 0.62 } as const;

const BODY = { x: 0.17, y: 0.17, z: 0.43, cy: 0.34, cz: -0.04 };
const HEAD = { x: 0.145, y: 0.13, z: 0.17, cy: 0.3, cz: 0.43 };
const LEG = { radius: 0.043, length: 0.17, frontZ: 0.24, rearZ: -0.28 };

function mesh(geometry: THREE.BufferGeometry, material: THREE.Material, name: string): THREE.Mesh {
  const result = new THREE.Mesh(geometry, material);
  result.name = name;
  result.castShadow = true;
  result.receiveShadow = true;
  return result;
}

function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

/**
 * A compact barrel, kept as one loft so its seam follows a real continuous back.
 *
 * `rings` and `segments` are arguments rather than constants because the herd
 * build cannot afford the sleeping pig's 8 x 10 and must not re-derive the
 * profile to say so. The shape — the rounded rump, the taper into the neck, the
 * flattened belly — is in the loop below and is the same at either density.
 */
function buildTorso(rings: number, segments: number): THREE.BufferGeometry {
  const positions: number[] = [];
  const indices: number[] = [];

  for (let i = 0; i <= rings; i++) {
    const u = i / rings;
    const cap = u < 0.16 ? Math.sqrt(Math.max(0, 1 - (1 - u / 0.16) ** 2)) : 1;
    const taper = 1 - 0.38 * smoothstep(0.7, 1, u);
    const z = BODY.cz - BODY.z + BODY.z * 1.78 * u;
    for (let j = 0; j <= segments; j++) {
      const theta = (j / segments) * Math.PI * 2;
      const c = Math.cos(theta);
      positions.push(
        BODY.x * cap * taper * Math.sin(theta),
        BODY.cy + BODY.y * cap * (c < 0 ? c * 1.08 : c),
        z,
      );
    }
  }

  for (let i = 0; i < rings; i++) {
    for (let j = 0; j < segments; j++) {
      const a = i * (segments + 1) + j;
      const b = a + segments + 1;
      indices.push(a, b, a + 1, a + 1, b, b + 1);
    }
  }

  const neckCentre = positions.length / 3;
  positions.push(0, BODY.cy, BODY.cz + BODY.z * 0.78);
  const lastRing = rings * (segments + 1);
  for (let j = 0; j < segments; j++) indices.push(neckCentre, lastRing + j + 1, lastRing + j);

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  return geometry;
}

function buildEar(side: 1 | -1, fabric: THREE.Material): THREE.Mesh {
  // A small flattened lobe; its forward droop is authored into the mesh transform
  // rather than exposed as a pivot, because this pig never wakes for the player.
  const ear = mesh(new THREE.SphereGeometry(1, 5, 3), fabric, side > 0 ? 'ear.L' : 'ear.R');
  ear.scale.set(0.07, 0.035, 0.09);
  ear.position.set(side * 0.09, HEAD.cy + 0.07, HEAD.cz + 0.045);
  ear.rotation.set(0.52, side * 0.22, side * -0.4);
  return ear;
}

interface LegOptions {
  /**
   * Hub-to-foot length. Defaults to `LEG.length`, which is a 0.17 m STUB and is
   * only correct for the sleeping pig: legs folded under a lying body never
   * reach a floor, so their length was free and was never a real measurement.
   * A standing build must say what it wants.
   */
  length?: number;
  /** Shaft radius at the foot; the hub end is 0.8 of it. Defaults to `LEG.radius`. */
  radius?: number;
  /** Sides on the shaft cylinder. */
  radialSegments?: number;
  /**
   * The rounded hoof lobe. On for the sleeping pig, where the folded feet are
   * held up at the viewer at reading distance; off for the herd, where it is 20
   * triangles per leg sitting in grass at eight metres in moonlight. The shaft
   * already flares towards the foot, which is what carries that read once the
   * lobe is gone.
   */
  trotter?: boolean;
}

function buildLeg(
  name: string,
  x: number,
  z: number,
  fold: number,
  fabric: THREE.Material,
  options: LegOptions = {},
): THREE.Group {
  const length = options.length ?? LEG.length;
  const radius = options.radius ?? LEG.radius;
  const radialSegments = options.radialSegments ?? 5;

  const leg = new THREE.Group();
  leg.name = name;
  leg.position.set(x, BODY.cy - 0.035, z);
  leg.rotation.x = fold;

  const shaft = new THREE.CylinderGeometry(radius * 0.8, radius, length, radialSegments, 1);
  shaft.translate(0, -length / 2, 0);
  leg.add(mesh(shaft, fabric, 'leg-shaft'));

  if (options.trotter !== false) {
    const trotter = new THREE.SphereGeometry(radius * 1.08, 5, 3);
    trotter.scale(1, 0.62, 1.18);
    trotter.translate(0, -length, 0.018);
    leg.add(mesh(trotter, fabric, 'trotter'));
  }
  return leg;
}

interface HeadDetail {
  /** `SphereGeometry` width and height segments for the skull. */
  head: readonly [number, number];
  /** …and for the snout stub. */
  snout: readonly [number, number];
}

/**
 * The slack head, its two ears and the snout, added straight into the assembly.
 *
 * There is no head pivot at either detail level: a finishing pig has no
 * attention beat, and the sleeping build's only motion channel is the breath.
 *
 * The placements here are the reason the herd is worth sharing code with. They
 * are all stated against `HEAD`, so the cheap pig carries its head at the same
 * height, the same drop and the same forward reach as the one in the hall; only
 * the tessellation differs, and that is the argument.
 */
function addHead(
  target: THREE.Group,
  fabric: THREE.Material,
  shadowFabric: THREE.Material,
  detail: HeadDetail,
): void {
  const head = mesh(new THREE.SphereGeometry(1, detail.head[0], detail.head[1]), fabric, 'head');
  head.scale.set(HEAD.x, HEAD.y, HEAD.z);
  head.position.set(0, HEAD.cy, HEAD.cz);
  head.rotation.x = -0.18;
  target.add(head, buildEar(1, fabric), buildEar(-1, fabric));

  const snout = mesh(new THREE.SphereGeometry(1, detail.snout[0], detail.snout[1]), shadowFabric, 'snout');
  snout.scale.set(0.105, 0.072, 0.065);
  snout.position.set(0, HEAD.cy - 0.045, HEAD.cz + HEAD.z * 0.82);
  target.add(snout);
}

/** Standing is the useful proportion frame; `build()` turns this entire assembly over. */
function buildStanding(): THREE.Group {
  const standing = new THREE.Group();
  standing.name = 'finisher-standing';

  const fabric = surface('fabric', FINISHER.fabric, {
    seam: true,
    seamWidth: 0.009,
    seamColour: FINISHER.seam,
  });
  const shadowFabric = surface('fabric', FINISHER.fabricShadow);
  const button = surface('fabric', FINISHER.button);
  standing.add(mesh(buildTorso(8, 10), fabric, 'body'));
  addHead(standing, fabric, shadowFabric, { head: [8, 5], snout: [6, 3] });

  for (const side of [1, -1] as const) {
    const eye = mesh(new THREE.SphereGeometry(0.016, 5, 3), button, side > 0 ? 'eye.L' : 'eye.R');
    eye.scale.z = 0.65;
    eye.position.set(side * 0.085, HEAD.cy + 0.025, HEAD.cz + 0.125);
    standing.add(eye);
  }

  // Folded pairs make the side-on silhouette read as resting limbs, not a table
  // tipped over. The upper pair falls forward; the lower pair tucks beneath.
  standing.add(
    buildLeg('leg.FL', 0.1, LEG.frontZ, -1.05, fabric),
    buildLeg('leg.FR', -0.1, LEG.frontZ, -0.72, fabric),
    buildLeg('leg.BL', 0.12, LEG.rearZ, 0.88, fabric),
    buildLeg('leg.BR', -0.12, LEG.rearZ, 0.58, fabric),
  );

  return standing;
}

function build(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'finisher';

  // At the floor and unrotated: systems may scale this node in Y only. That keeps
  // the measured contact plane fixed while the lying body's back-to-belly axis
  // expands upward, so a breath never makes the heap sink into the slats.
  const breath = new THREE.Object3D();
  breath.name = 'breath';
  group.add(breath);

  const lie = new THREE.Object3D();
  lie.name = 'lie';
  lie.rotation.z = Math.PI / 2;
  breath.add(lie);
  lie.add(buildStanding());

  // Measure the constructed geometry after the rotation. This is intentionally
  // not derived from standing dimensions: folded legs and fallen ears make the
  // contact and visual centre properties of the actual mesh, not of its sketch.
  group.updateMatrixWorld(true);
  const bounds = new THREE.Box3().setFromObject(group);
  lie.position.y -= bounds.min.y;
  lie.position.x -= (bounds.min.x + bounds.max.x) / 2;
  lie.position.z -= (bounds.min.z + bounds.max.z) / 2;

  return group;
}

export interface FinisherParts {
  /** Scale this node on Y only; it is the asset's only exposed motion channel. */
  breath: THREE.Object3D;
}

function required(root: THREE.Object3D, name: string): THREE.Object3D {
  const found = root.getObjectByName(name);
  if (!found) throw new Error(`finisher: rig node "${name}" missing — was the mesh renamed?`);
  return found;
}

export function findFinisherParts(root: THREE.Object3D): FinisherParts {
  return { breath: required(root, 'breath') };
}

export const finisherAsset: AssetDefinition = {
  id: 'finisher',
  label: 'Finishing pig',
  description: 'A six-month finishing pig asleep in the fattening hall, built standing and laid onto its side.',
  // The lying footprint, and its width is MEASURED rather than derived.
  //
  // The tempting derivation is "standing height becomes across-pen width", which
  // is what was declared first and is wrong by 0.21 m: a pig asleep on its side
  // folds its legs, so the animal that stood 0.62 m tall lies only 0.41 m across.
  // `asset.spec.ts` rejected the derivation, and it was right to — `footprint`
  // frames the acceptance shots, so it has to describe the mesh that exists and
  // not the upright pig it began as.
  footprint: { width: 0.41, height: FINISHER_SCALE.width, length: FINISHER_SCALE.length },
  create: build,
};

// ─────────────────────────────────────────────────────────────────────────────
// The herd pig: the same animal, on its feet, at a twelfth of the attention.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The standing leg, and it is deliberately not `LEG.length`.
 *
 * `LEG.length` is 0.17 m because the sleeping pig's legs are folded and hidden;
 * nothing ever measured them. Standing, the leg is the entire difference between
 * a pig and a pig lying down, and `FINISHER_SCALE.height` already declares what
 * that difference has to come to. So it is derived rather than guessed: the leg
 * hangs from a hub at `BODY.cy - 0.035` and the back sits at `BODY.cy + BODY.y`,
 * so the length that puts the declared 0.62 m back on a 0 m floor is
 * `height - (BODY.y + 0.035)` = 0.415 m. Writing 0.415 as a literal instead
 * would let the two disagree the first time a pig is reproportioned.
 *
 * The radius is widened 15 %. The sleeping shaft is 2.4x shorter at the same
 * width; carrying that width over gives a barrel on four wires, and the herd is
 * seen at 8-40 m where a wire is one pixel and the animal reads as floating.
 */
const HERD_LEG = {
  length: FINISHER_SCALE.height - (BODY.y + 0.035),
  radius: LEG.radius * 1.15,
} as const;

/**
 * A few degrees of A-stance, front feet back and rear feet forward.
 *
 * Four vertical posts under a barrel is a table, and twelve tables in a hedgerow
 * is a woodpile. The magnitude is the same on all four so every foot still lands
 * on the same plane — the origin contract is checked to 1 mm and an asymmetric
 * splay spends all of that on nothing.
 */
const HERD_SPLAY = 0.06;

/**
 * ── Nothing on this asset can move, and that is the room's doing ────────────
 *
 * Beat 10 puts twelve of these in one hedgerow as a single instanced batch,
 * which means `mergeBySurface` (`src/world/instancing.ts`): every mesh sharing a
 * surface is merged into one geometry before the batch is built, so a child
 * pivot is baked into its parent and stops existing.
 *
 * So there is no `breath` node and no `findHerdParts()`, and their absence is
 * the design. The sleeping `finisher` has both; this one cannot have them,
 * because a handle that silently does nothing is worse than no handle at all.
 * Motion for the herd is carried ENTIRELY by the root transform, exactly as
 * `henFlock.ts` records of the hens — their panic is a gait, a yaw and a hop
 * because a merged bird cannot flap. Anyone who adds a moving part here will get
 * a rigid one, in the room only, with no error anywhere.
 *
 * ── The triangle budget is the whole brief ─────────────────────────────────
 *
 * `finisher` is 498 triangles of geometry. Twelve of those is ~6 000, and the
 * hedgerow census multiplies geometry by roughly four — beauty pass, the post
 * stack's normal/depth target, and the shadow map — so it is ~23 900 against
 * 5 700 of headroom (`tests/e2e/render-budget.spec.ts`). The ceiling set for
 * this asset was therefore 350, and it comes in at 257:
 *
 *     torso   6 rings x 7 segments      6*7*2 = 84, + a 7-triangle neck fan   91
 *     head    sphere 6 x 4              2*6*(4-1)                             36
 *     ears    2 x sphere 5 x 3          2 * 2*5*(3-1)                         40
 *     snout   sphere 5 x 2              2*5*(2-1)                             10
 *     legs    4 x 5-sided cylinder      4 * (5*2 side + 5 + 5 caps)           80
 *                                                                           ---
 *                                                                            257
 *
 * **That 257 is geometry, and the workbench will print 636.** `stats()` in
 * `src/preview/main.ts` reports `renderer.info.render.triangles`, which is the
 * whole SCENE for the last frame: the asset in the beauty pass, the asset again
 * in the shadow map, and 122 triangles of scale rod and shadow catcher. The
 * sleeping `finisher` is 498 triangles of geometry and prints 1 118, and
 * 498*2 + 122 is exactly 1 118 — so anything comparing an asset against a
 * triangle budget must halve the workbench number and take the 122 off first, or
 * it will condemn a mesh for the grid it is standing on.
 *
 * **257 x 12 x 4 is still ~12 300 and the headroom is still 5 700.** Cutting the
 * mesh further does not close that: the leanest pig still recognisable as one
 * costs about 134 triangles and lands at ~6 400, over on its own, having spent
 * the silhouette to get there. **The lever is the room's ceiling, not the
 * asset**, and `render-budget.spec.ts` says in as many words that stage 4 must
 * come back to it with a measurement. This asset's job was to make that raise
 * as small as it can honestly be.
 *
 * The cuts are chosen against one read and no other: it is night, it is 8-40 m,
 * it is one of twelve in a huddle, and **the player is meant to be able to count
 * them**. So the barrel, the low head, the two ears and four legs holding the
 * body clear of the grass all stay, and the eyes — 40 triangles, of which two
 * are always facing away — do not. Losing them also loses the `button` surface,
 * which is the point: each surface is one `InstancedMesh` and therefore about
 * 3.4 draw calls for the whole herd, measured in the wool shed.
 */
function buildHerd(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'finisher-herd';

  const fabric = surface('fabric', FINISHER.fabric, {
    seam: true,
    seamWidth: 0.009,
    seamColour: FINISHER.seam,
  });
  const shadowFabric = surface('fabric', FINISHER.fabricShadow);

  group.add(mesh(buildTorso(6, 7), fabric, 'body'));
  addHead(group, fabric, shadowFabric, { head: [6, 4], snout: [5, 2] });

  const leg: LegOptions = {
    length: HERD_LEG.length,
    radius: HERD_LEG.radius,
    radialSegments: 5,
    trotter: false,
  };
  group.add(
    buildLeg('leg.FL', 0.1, LEG.frontZ, HERD_SPLAY, fabric, leg),
    buildLeg('leg.FR', -0.1, LEG.frontZ, HERD_SPLAY, fabric, leg),
    buildLeg('leg.BL', 0.12, LEG.rearZ, -HERD_SPLAY, fabric, leg),
    buildLeg('leg.BR', -0.12, LEG.rearZ, -HERD_SPLAY, fabric, leg),
  );

  // The flat-kit seating, not the sleeping build's inline version: that one
  // exists to offset a nested pose rig, and this asset deliberately has no node
  // to offset. `seatOnOrigin` measures what was built and moves the children,
  // which is the same measurement by a shorter route.
  seatOnOrigin(group);
  return group;
}

export const finisherHerdAsset: AssetDefinition = {
  id: 'finisher-herd',
  label: 'Finishing pig (herd)',
  description: 'A finishing pig standing, at herd detail: 257 geometry triangles for twelve in one instanced batch.',
  // MEASURED, by building the group and reading `new THREE.Box3().setFromObject`
  // — not derived, for the reason recorded on `finisherAsset` above, where the
  // obvious derivation was wrong by 0.21 m and the acceptance gate caught it.
  //
  // The height is 2 mm over `FINISHER_SCALE.height` rather than on it, and that
  // is the derivation being right rather than wrong: `HERD_LEG.length` puts the
  // back at 0.620, then the splay tips each foot disc so its RIM is the lowest
  // point and the animal is seated on the rim. Declaring 0.620 here would be
  // declaring the sketch again.
  footprint: { width: 0.341, height: 0.622, length: 1.101 },
  create: buildHerd,
};
